import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box } from "@material-ui/core";
import { ErrorModal } from "../../components/ModalAlert";
import { LoaderSpinner } from "../../components/spinner/loader";
import supabase from "../../supabase";
import RedButton from "../../components/buttons/redButton";
import { formatCurrency, formatCurrencyManual } from "../../utils/formatters";
import { currencyValues } from "../../constants/paymentValues";

const TicketDetailsForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [orders, setOrders] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    // Obtener datos del ticket desde state o desde parámetros de URL
    let ticketData = null;
    
    if (location?.state?.ticket) {
      // Si viene desde state (navegación interna)
      ticketData = location.state.ticket;
    } else if (location?.search) {
      // Si viene desde parámetros de URL (nueva pestaña)
      const urlParams = new URLSearchParams(location.search);
      const ticketId = urlParams.get('ticketId');
      const ticketNumber = urlParams.get('ticketNumber');
      const ticketType = urlParams.get('ticketType');
      const createdAt = urlParams.get('createdAt');
      
      if (ticketId) {
        ticketData = {
          ticketId: parseInt(ticketId),
          ticketNumber: ticketNumber,
          ticketType: ticketType,
          createdAt: createdAt
        };
      }
    }
    
    if (!ticketData) {
      ErrorModal({
        title: "Error",
        text: "No se encontró el ticket seleccionado",
      });
      navigate(-1);
      return;
    }

    if (!ticketData.ticketId) {
      ErrorModal({
        title: "Error",
        text: "El ticket no tiene un ID válido",
      });
      navigate(-1);
      return;
    }

    // Guardar los datos del ticket para usar en getTicketData
    setTicket(ticketData);
    getTicketData(ticketData.ticketId);
  }, [location?.state?.ticket, location?.search]);

      const getTicketData = async (ticketId) => {
    setLoading(true);
    try {
      
      // Primero obtenemos los detalles del ticket
      const { data: ticketData, error: ticketError } = await supabase
        .from("tickets")
        .select(`
          *,
          client:clientId (
            company,
            cuit
          ),
          ticketDetails (
            *
          )
        `)
        .eq("ticketId", ticketId)
        .single();

      if (ticketError) {
        console.error('Error al obtener ticket:', ticketError);
        throw ticketError;
      }
      if (!ticketData) {
        console.error('No se encontró ticket con ID:', ticketId);
        throw new Error("No se encontró el ticket");
      }

      setTicket(ticketData);

      // Luego obtenemos los datos de las órdenes asociadas al ticket
      const { data: ordersData, error: ordersError } = await supabase
        .from("orders")
        .select(`
          *,
          orderDetails (
            price,
            quantity,
            ivaPercentage,
            description,
            currency
          )
        `)
        .eq("ticketId", ticketId);

      if (ordersError) {
        console.error('Error al obtener órdenes:', ordersError);
        throw ordersError;
      }

      // Verificar si hay órdenes asociadas al ticket
      if (!ordersData || ordersData.length === 0) {
        // Hacer una consulta adicional para verificar si hay órdenes en la tabla
        const { data: allOrders, error: allOrdersError } = await supabase
          .from("orders")
          .select("orderId, ticketId")
          .eq("ticketId", ticketId);
        
        if (allOrdersError) {
          console.error('Error al verificar órdenes:', allOrdersError);
        }
      }
      
      // Si no hay órdenes, inicializamos con un array vacío
      const ordersToProcess = ordersData || [];

      // Combinamos los datos de orderDetails con los datos de las órdenes
      const processedOrders = ordersToProcess.map(order => {
        // Calculamos el subtotal sumando todos los detalles de la orden
        const subtotal = order.orderDetails?.reduce((sum, detail) => {
          const detailSubtotal = (detail.price || 0) * (detail.quantity || 0) * (detail.currency === currencyValues.find(c => c.label === 'ARS').value ? 1 : (order.currencyRate || 1)) * (1 + ((detail.ivaPercentage || 0) / 100));
          return sum + detailSubtotal;
        }, 0) || 0;

        return {
          ...order,
          orderDetails: order.orderDetails || [],
          subtotal
        };
      });

      setOrders(processedOrders);
      
      const total = processedOrders.reduce((sum, order) => sum + order.subtotal, 0);
      setTotalAmount(total);
    } catch (error) {
      console.error('Error en getTicketData:', error);
      ErrorModal({
        title: "Error obteniendo los datos del ticket",
        text: error.message,
      });
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  return loading ? (
    <div
      style={{
        width: "100%",
        height: "200px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <LoaderSpinner />
    </div>
  ) : (
    <Box className="order-details-form-container" style={{ width: "100%", margin: "0 auto", padding: "20px" }}>
      <Box className="order-detail-form-item-container head-order-container" style={{ height: "auto" }}>
        <Box className="first-item-container">
          <h4 style={{ fontSize: "24px", marginBottom: "20px" }}>Datos de la Factura</h4>
          <Box className="head-order-detail-one" style={{ 
            display: "grid", 
            gridTemplateColumns: "1fr 1fr 1fr", 
            gap: "20px"
          }}>
            <Box className="item" style={{ 
              padding: "15px", 
              backgroundColor: "#f5f5f5", 
              borderRadius: "5px", 
              minWidth: "300px"
            }}>
              <h4 style={{ margin: 0 }}>{ticket?.ticketType} - Nro {ticket?.ticketNumber}</h4>
            </Box>
            <Box className="item" style={{ 
              padding: "15px", 
              backgroundColor: "#f5f5f5", 
              borderRadius: "5px", 
              minWidth: "400px"
            }}>
              <h4 style={{ margin: 0 }}>Cliente: {ticket?.client?.company} - CUIT: {ticket?.client?.cuit}</h4>
            </Box>
            <Box className="item" style={{ 
              padding: "15px", 
              backgroundColor: "#f5f5f5", 
              borderRadius: "5px", 
              minWidth: "200px",
              textAlign: "right"
            }}>
              <h4 style={{ margin: 0 }}>Fecha: {new Date(ticket?.createdAt).toLocaleDateString()}</h4>
            </Box>
          </Box>
        </Box>
      </Box>
      {orders.length > 0 ? (
        orders.map((order, index) => (
          <Box key={index} className="order-detail-form-item-container" style={{ marginBottom: "30px", border: "1px solid #e0e0e0", borderRadius: "8px", padding: "20px", height: "auto" }}>
            <Box className="first-item-container">
              <h4 style={{ fontSize: "20px", marginBottom: "15px" }}>Orden N° {order.orderId}</h4>
              <Box className="order-details-list">                
                <Box className="order-detail-form-item-container" style={{ backgroundColor: "#f5f5f5", borderRadius: "5px", height: "fit-content" }}>
                  <Box className="order-detail-item" style={{ 
                    display: "grid", 
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", 
                    gap: "15px",
                    backgroundColor: "#f9f9f9",
                    borderRadius: "5px",
                    height: "fit-content"
                  }}>
                    <Box className="item" style={{ height: "fit-content" }}>
                      <h4>Cantidad</h4>
                    </Box>
                    <Box className="item" style={{ height: "fit-content" }}>
                      <h4>Descripción</h4>
                    </Box>
                    <Box className="item" style={{ height: "fit-content" }}>
                      <h4>Precio Unitario</h4>
                    </Box>
                    <Box className="item" style={{ height: "fit-content" }}>
                      <h4>Cotización</h4>
                    </Box>
                    <Box className="item" style={{ height: "fit-content" }}>
                      <h4>IVA%</h4>
                    </Box>
                    <Box className="item" style={{ height: "fit-content" }}>
                      <h4>Subtotal</h4>
                    </Box>
                  </Box>
                </Box>
                {order.orderDetails.map((detail, detailIndex) => (
                  <Box key={detailIndex} className="order-detail-item" style={{ 
                    display: "grid", 
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", 
                    gap: "15px",
                    padding: "15px",
                    backgroundColor: "#f9f9f9",
                    borderRadius: "5px",
                    marginBottom: "10px",
                    height: "fit-content"
                  }}>
                    <Box className="item" style={{ height: "fit-content" }}>
                      <h4>{detail.quantity}</h4>
                    </Box>
                    <Box className="item" style={{ height: "fit-content" }}>
                      <h4>{detail.description}</h4>
                    </Box>
                    <Box className="item" style={{ height: "fit-content" }}>
                      <h4>{formatCurrencyManual(detail.price)}</h4>
                    </Box>
                    <Box className="item" style={{ height: "fit-content" }}>
                      <h4>$ {detail.currency === currencyValues.find(c => c.label === 'ARS').value ? 1 : (order.currencyRate || 1)}</h4>
                    </Box>
                    <Box className="item" style={{ height: "fit-content" }}>
                      <h4>{detail.ivaPercentage}%</h4>
                    </Box>
                    <Box className="item" style={{ height: "fit-content" }}>
                      <h4>{formatCurrency(Math.round((detail.price || 0) * (detail.quantity || 0) * (detail.currency === currencyValues.find(c => c.label === 'ARS').value ? 1 : (order.currencyRate || 1)) * (1 + ((detail.ivaPercentage || 0) / 100)) * 100) / 100)}</h4>
                    </Box>
                  </Box>
                ))}
              </Box>
              <Box className="order-total" style={{ 
                marginTop: "20px", 
                padding: "15px", 
                backgroundColor: "#e3f2fd", 
                borderRadius: "5px",
                textAlign: "right",
                height: "fit-content"
              }}>
                <h4 style={{ fontSize: "18px", fontWeight: "bold" }}>Total de la Orden: {formatCurrency(order.subtotal)}</h4>
              </Box>
            </Box>
          </Box>
        ))
      ) : (
        <Box className="order-detail-form-item-container" style={{ marginTop: "20px", padding: "20px", backgroundColor: "#f5f5f5", borderRadius: "5px", height: "fit-content" }}>
          <Box className="first-item-container">
            <h4>No hay órdenes asociadas a esta factura</h4>
            <p style={{ color: "#666", marginTop: "10px" }}>
              Esta factura (ID: {ticket?.ticketId}) no tiene órdenes de trabajo asociadas.
            </p>
          </Box>
        </Box>
      )}

      {orders.length > 0 && (
        <Box className="order-detail-form-item-container" style={{ 
          marginTop: "30px", 
          padding: "20px", 
          backgroundColor: "#e8f5e9", 
          borderRadius: "8px",
          textAlign: "right",
          height: "fit-content"
        }}>
          <Box className="first-item-container">
            <h4 style={{ fontSize: "24px", fontWeight: "bold" }}>Total de la Factura: {formatCurrency(totalAmount)}</h4>
          </Box>
        </Box>
      )}


    </Box>
  );
};

export default TicketDetailsForm; 